<template>
    <div class="nominee-director-create-or-edit">
        <b-sidebar
            id="sidebar-task-handler"
            data-test="sidebar"
            sidebar-class="sidebar-lg"
            :visible="isNomineeDirectorSidebarActive"
            bg-variant="white"
            shadow
            backdrop
            no-header
            right
            @change="
                (val) => $emit('update:is-nominee-director-sidebar-active', val)
            "
            @hidden="clearFormData"
        >
            <template #default="{ hide }">
                <!-- Header -->
                <div
                    class="
                        d-flex
                        justify-content-between
                        align-items-center
                        content-sidebar-header
                        px-2
                        py-1
                        side-bar-header
                    "
                >
                    <h3 class="mb-0">
                        {{ sideBarTitle }}
                    </h3>

                    <div>
                        <feather-icon
                            class="ml-1 cursor-pointer"
                            icon="XIcon"
                            size="16"
                            @click="hide"
                        />
                    </div>
                </div>

                <!-- Body -->
                <validation-observer
                    v-slot="{ handleSubmit }"
                    ref="refFormObserver"
                >
                    <!-- Form -->
                    <b-form
                        class="p-2"
                        @submit.prevent="handleSubmit(onSubmit)"
                        @reset.prevent="resetForm"
                    >
                        <!-- Title -->
                        <validation-provider
                            v-slot="{ errors }"
                            name="nominee director name"
                            rules="required"
                        >
                            <b-form-group label="Nominee director name" :state="errors.length > 0 ? false : null">
                                <v-select
                                    id="nominee-director-name"
                                    ref="nomineeDirector"
                                    v-model="nomineeDirectorLocal.nomineeDirector"
                                    class="custom-select-icon"
                                    placeholder="Select nominee director"
                                    label="name" 
                                    :clearable="false"
                                    :disabled = "isDisabled || dataStatus === 'edit'"
                                    :options="nomineeDirectorOptions"
                                >
                                    <div slot="no-options" class="d-flex justify-content-center">
                                        <normal-loader v-if="nomineeDirectorOptions.length <= 0" />
                                        <span v-else> {{ NO_MATCHING_OPTIONS }} </span>
                                    </div>
                                </v-select>
                                <small class="text-danger">{{ errors[0] }}</small>
                            </b-form-group>
                        </validation-provider>
                        
                        <validation-provider
                            v-slot="{ errors }"
                            name="nominator"
                            rules="required"
                        >
                            <b-form-group label="Nominator type" :state="errors.length > 0 ? false : null">
                                <v-select
                                    id="nominatorType"
                                    ref="nominatorType"
                                    v-model="nomineeDirectorLocal.nominatorType"
                                    class="custom-select-icon text-capitalize"
                                    placeholder="Select nominator type"
                                    label="text" 
                                    :clearable="false"
                                    :disabled = "isDisabled"
                                    :options="nominatorTypeOptions"
                                    @input="onNominatorTypeChange"
                                >
                                    <div slot="no-options" class="d-flex justify-content-center">
                                        <normal-loader v-if="nominatorTypeOptions.length <= 0" />
                                        <span v-else> {{ NO_MATCHING_OPTIONS }} </span>
                                    </div>
                                </v-select>
                                <small class="text-danger">{{ errors[0] }}</small>
                            </b-form-group>
                        </validation-provider>

                        <validation-provider
                            v-slot="{ errors }"
                            name="appointment date"
                            rules="required"
                            >
                            <b-form-group label="Date on which the director became the nominee" :state="errors.length > 0 ? false : null">
                                <flat-pickr
                                    v-model="nomineeDirectorLocal.appointmentDate"
                                    placeholder="Pick a date"
                                    class="form-control"
                                    :config="datePickerConfig"
                                    :disabled = "isDisabled"
                                    :style="dynamicStyle"
                                    data-test="appointment-date"
                                />
                                <small class="text-danger">{{ errors[0] }}</small>
                            </b-form-group>
                        </validation-provider>

                        <validation-provider
                            v-slot="{ errors }"
                            name="cessation date"
                            >
                                <b-form-group label="Date on which the director ceased to be the nominee" :state="errors.length > 0 ? false : null">
                                    <flat-pickr
                                        v-model="nomineeDirectorLocal.cessationDate"
                                        placeholder="Pick a date"
                                        class="form-control"
                                        :config="datePickerConfig"
                                        :disabled = "isDisabled"
                                        :style="dynamicStyle"
                                        data-test="cessation-date"
                                    />
                                <small class="text-danger">{{ errors[0] }}</small>
                                </b-form-group>
                        </validation-provider>

                        <validation-provider
                            v-slot="{ errors }"
                            :name="dynamicNameLabel.toLowerCase()"
                            rules="required"
                            mode="lazy"
                        >
                            <b-form-group
                                :label="dynamicNameLabel"
                                :state="errors.length > 0 ? false : null"
                            >
                                <v-select
                                    id="name"
                                    ref="nominatorName"
                                    v-model="nomineeDirectorLocal.nominator"
                                    :placeholder="dynamicNamePlaceholder"
                                    :state="errors.length > 0 ? false : null"
                                    data-test="nominator-name"
                                    class="custom-select-icon text-capitalize"
                                    :clearable="false"
                                    label="text"
                                    :disabled = "isDisabled"
                                    :options="nominators"
                                    @input = "onNominatorNameChange"
                                >
                                    <div slot="no-options" class="d-flex justify-content-center">
                                        <normal-loader v-if="nominators.length <= 0" />
                                        <span v-else> {{ NO_MATCHING_OPTIONS }} </span>
                                    </div>
                                </v-select>
                                <small class="text-danger">{{ errors[0] }}</small>
                            </b-form-group>
                        </validation-provider>

                        <!-- < INDIVIDUAL NOMINATOR > -->
                        <div v-if="isNominatorTypeIndividual">
                            <validation-provider
                                v-slot="validationContext"
                                name="individual nominator aliases"
                            >
                                <b-form-group label="Individual nominator aliases, if any">
                                    <b-form-input
                                        id="name"
                                        ref="nominatorAlias"
                                        v-model="nomineeDirectorLocal.alias"
                                        placeholder="individual nominator aliases"
                                        :state="
                                            getValidationState(validationContext)
                                        "
                                        :disabled = "isDisabled"
                                        trim
                                        data-test="alias"
                                    />
                                    <b-form-invalid-feedback>
                                        {{ validationContext.errors[0] }}
                                    </b-form-invalid-feedback>
                                </b-form-group>
                            </validation-provider>
                        </div>
                        <!-- </ INDIVIDUAL NOMINATOR > -->

                        <!-- < CORPORATE NOMINATOR > -->
                        <div v-if="! isNominatorTypeIndividual">
                            <validation-provider
v-slot="{ errors }"
                                name="corporate nominator UEN"
                                rules="required"
                            >
                                <b-form-group label="Corporate nominator UEN issued by the registrar (ACRA)">
                                    <b-form-input
                                        id="name"
                                        v-model="nomineeDirectorLocal.uen"
                                        placeholder="Enter corporate nominator UEN"
                                        :state="errors.length > 0 ? false : null"
                                        data-test="uen"
                                        :disabled = "isDisabled"
                                    />
                                    <small class="text-danger">{{ errors[0] }}</small>
                                </b-form-group>
                            </validation-provider>

                            <validation-provider
                                v-slot="{ errors }"
                                name="corporate nominator legal form"
                                rules="required"
                            >
                                <b-form-group
                                    label="Corporate nominator legal form"
                                    :state="errors.length > 0 ? false : null"
                                >
                                    <v-select
                                        ref="legalForm"
                                        v-model="nomineeDirectorLocal.legalForm"
                                        placeholder="Select corporate nominator legal form"
                                        class="custom-select-icon text-capitalize"
                                        :clearable="false"
                                        :disabled = "isDisabled"
                                        :options="legalForms"
                                        label="text"
                                    >
                                        <div slot="no-options" class="d-flex justify-content-center">
                                            <normal-loader v-if="legalForms.length <= 0" />
                                            <span v-else> {{ NO_MATCHING_OPTIONS }} </span>
                                        </div>
                                    </v-select>
                                    <small class="text-danger">{{ errors[0] }}</small>
                                </b-form-group>
                            </validation-provider>

                            <validation-provider
v-slot="{ errors }"
                                name="corporate nominator jurisdiction"
                                rules="required"
                            >
                                <b-form-group label="Corporate nominator jurisdiction" :state="errors.length > 0 ? false : null">
                                        <v-select
                                        ref ="jurisdiction"
                                        v-model="nomineeDirectorLocal.jurisdiction"
                                        :clearable="false"
                                        :options="jurisdictions"
                                        label="name"
                                        value="id"
                                        :disabled = "isDisabled"
                                        class="custom-select-icon text-capitalize"
                                        placeholder="Select corporate nominator jurisdiction"
                                        @input="onNominatorJurisdictionChange"
                                    >
                                        <div slot="no-options" class="d-flex justify-content-center">
                                            <normal-loader v-if="jurisdictions.length <= 0" />
                                            <span v-else> {{ NO_MATCHING_OPTIONS }} </span>
                                        </div>
                                    </v-select>
                                    <small class="text-danger">{{ errors[0] }}</small>
                                </b-form-group>
                            </validation-provider>

                            <validation-provider
v-slot="{ errors }"
                                name="corporate nominator statue"
                                rules="required"
                                mode="lazy"
                            >
                                <b-form-group label="Corporate nominator statue under which the corporate is formed" :state="errors.length > 0 ? false : null">
                                    <v-select
                                        ref="statue"
                                        v-model="nomineeDirectorLocal.statue"
                                        placeholder="Select corporate nominator statue"
                                        class="custom-select-icon text-capitalize"
                                        :clearable="false"
                                        label="name"
                                        :disabled = "isDisabled"

                                        value="id"
                                        :options="statues"
                                        :state="errors.length > 0 ? false : null"
                                    >
                                        <div slot="no-options" class="d-flex justify-content-center">
                                            <normal-loader v-if="statues.length <= 0" />
                                            <span v-else> {{ NO_MATCHING_OPTIONS }} </span>
                                        </div>
                                    </v-select>
                                    <small class="text-danger">{{ errors[0] }}</small>
                                </b-form-group>
                            </validation-provider>

                            <validation-provider
v-slot="{ errors }"
                                name="registrar"
                                rules="required"
                                mode="lazy"
                            >
                                <b-form-group :state="errors.length > 0 ? false : null" label="Name of corporate nominator's register of the jurisdiction where the legal entity is formed or incorporated">
                                    <v-select
                                        ref="nominatorRegistrar"
                                        v-model="nomineeDirectorLocal.registrar"
                                        class="custom-select-icon text-capitalize"
                                        :clearable="false"
                                        :disabled = "isDisabled"
                                        label="name"
                                        value="id"
                                        :options="registrars"
                                        :state="errors.length > 0 ? false : null"
                                        placeholder="Select corporate registrar"
                                    >
                                        <div slot="no-options" class="d-flex justify-content-center">
                                            <normal-loader v-if="registrars.length <= 0" />
                                            <span v-else> {{ NO_MATCHING_OPTIONS }} </span>
                                        </div>
                                    </v-select>
                                    <small class="text-danger">{{ errors[0] }}</small>
                                </b-form-group>
                            </validation-provider>
                        </div>
                        <!-- < /CORPORATE NOMINATOR > -->

                        <validation-provider
                            v-slot="{ errors }"
                            name="remark"
                        >
                            <b-form-group label="Remarks">
                                <b-form-input
                                    id="remark"
                                    v-model="nomineeDirectorLocal.remark"
                                    placeholder="Enter nominee director remark"
                                    :state="errors.length > 0 ? false : null"
                                    :disabled = "isDisabled"
                                    data-test="remark"
                                />
                                <small class="text-danger">{{ errors[0] }}</small>
                            </b-form-group>
                        </validation-provider>

                        <!-- Form Actions -->
                        <div class="d-flex">
                            <b-button
                                v-if="! isDisabled"
                                v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                                :disabled="dataSidebarActionLoading"
                                variant="primary"
                                class="
                                    mr-1
                                    mt-1
                                    d-flex
                                    justify-content-center
                                    align-items-center
                                "
                                type="submit"
                                data-test="create-nominee-director-btn"
                                >
                                <span :class="dataSidebarActionLoading ? 'mr-1' : ''">
                                    {{ dataStatus === 'edit' ? "Update" : "Add " }}
                                </span>

                                <img
                                    v-if="dataStatus === 'create' && !dataSidebarActionLoading"
                                    :src="'/illustrations/add-circle.svg'"
                                    class="ml-1"
                                    alt="add-logo"
                                />

                                <img
                                    v-if="dataStatus === 'edit' && !dataSidebarActionLoading"
                                    :src="'/illustrations/edit.svg'"
                                    class="ml-1"
                                    alt="edit-logo"
                                />
                                <normal-loader v-if="dataSidebarActionLoading" class="action-button left-10%" />
                            </b-button>

                            <b-button
                                v-ripple.400="'rgba(186, 191, 199, 0.15)'"
                                class="
                                mt-1 
                                d-flex
                                justify-content-center
                                align-items-center"
                                variant="outline-primary"
                                data-test="cancel"
                                @click="$emit('update:is-nominee-director-sidebar-active', false)"
                                >
                                Cancel
                                <vector 
                                    data-type="icons" 
                                    data-width="15px" 
                                    data-height="15px" 
                                    data-name="cancel" 
                                    class="float-right ml-1"
                                    alt="cancel-logo"
                                />
                            </b-button>
                        </div>
                    </b-form>
                </validation-observer>
            </template>
        </b-sidebar>
    </div>
</template>

<script>
import {
    BSidebar,
    BForm,
    BFormGroup,
    BFormInput,
    BButton,
    BFormInvalidFeedback,
} from "bootstrap-vue";
import flatPickr from "vue-flatpickr-component";
import vSelect from "vue-select";
import Ripple from "vue-ripple-directive";
import { ValidationProvider, ValidationObserver } from "vee-validate";
import { required, email } from "@validations";
import formValidation from "@core/comp-functions/forms/form-validation";
import { toRefs, ref, watch } from "@vue/composition-api";
import { debounce } from 'debounce';
import Shareholder from "@/models/Shareholder";
import { NOMINEE_DIRECTOR, SHAREHOLDER } from "@/constants/config";
import Nominator from '@/models/Nominator';
import Director from '@/models/Director';
import CompanyNomineeDirector from '@/models/CompanyNomineeDirector';
import { NO_MATCHING_OPTIONS } from "@/constants/errors";

export default {
    components: {
        // BSV
        BForm,
        vSelect,
        BButton,
        BSidebar,
        flatPickr,
        BFormGroup,
        BFormInput,
        BFormInvalidFeedback,

        // Form Validation
        ValidationProvider,
        ValidationObserver,
    },

    directives: {
        Ripple,
    },

    model: {
        prop: "isNomineeDirectorSidebarActive",
        event: "update:is-nominee-director-sidebar-active",
    },
    props: {
        isNomineeDirectorSidebarActive: {
            type: Boolean,
            required: true,
        },

        dataSidebarActionLoading: {
            type: Boolean,
            default: false,
        },

        dataNomineeDirector: {
            type: Object,
            required: true,
        },
        
        dataStatus: { 
            type: String,
            default: "view"
        },

        dataAutoFillRequestedCorporateNominator: {
            type: Object,
            default: null,
        }
    },

    data() {
        return {
            required,
            email,

            datePickerConfig: {
                // altInput: true,
                altFormat: "j M Y",
                dateFormat: "j M Y",
                disableMobile: "true"
            },

            dataShareholders: [],

            statues: [],
            dataStatues: [],
            registrars: [],
            dataRegistrars: [],
            legalForms: [],
            nominators: [],
            jurisdictions: [],

            nomineeDirectorOptions: [],
            nominatorTypeOptions: [],

            NO_MATCHING_OPTIONS,
        };
    },

    computed: {
        isDisabled() {
            if(this.dataStatus === "view")
                return true;
            return false;
        },

        sideBarTitle() {
            if(this.dataStatus === "create")
                return "Add Nominee Director";

            if(this.dataStatus === "view")
                return "Nominee Director Details";

            return "Update Nominee Director";
        },

        dynamicStyle() {
            if(this.dataStatus === "view")
                return "background: #efefef; cursor: context-menu!important";
            return "background: white;";
        },

        existNominators()
        {
            return this.nominators.filter(shareholder => !! shareholder).length >= 1;
        },
        
        isNominatorTypeIndividual() {
            // if there is no type yet, act like it's Individual
            if (! this.nomineeDirectorLocal.nominatorType) return true;

            return (
                this.nomineeDirectorLocal.nominatorType &&
                this.nomineeDirectorLocal.nominatorType.text === NOMINEE_DIRECTOR.NOMINATOR.TYPE.INDIVIDUAL
            );
        },

        dynamicIdLabel() {
            if (
                this.nomineeDirectorLocal.nominatorType &&
                this.nomineeDirectorLocal.nominatorType.text === NOMINEE_DIRECTOR.NOMINATOR.TYPE.CORPORATE
            )
                return "Corporate nominator ID on the corporate entity register of the jurisdiction where the corporate controller is formed or incorporated";

            return "Individual nominator ID";
        },

        dynamicIdPlaceholder() {
            if (
                this.nomineeDirectorLocal.nominatorType &&
                this.nomineeDirectorLocal.nominatorType.text === NOMINEE_DIRECTOR.NOMINATOR.TYPE.CORPORATE
            )
                return "Enter corporate nominator id";

            return "Enter individual nominator id";
        },

        dynamicNameLabel() {
            if (
                this.nomineeDirectorLocal.nominatorType &&
                this.nomineeDirectorLocal.nominatorType.text === NOMINEE_DIRECTOR.NOMINATOR.TYPE.CORPORATE
            )
                return "Corporate nominator name";

            return "Individual nominator name";
        },

        dynamicNamePlaceholder() {
            if (
                this.nomineeDirectorLocal.nominatorType &&
                this.nomineeDirectorLocal.nominatorType.text === NOMINEE_DIRECTOR.NOMINATOR.TYPE.CORPORATE
            )
                return "Select corporate nominator";

            return "Select individual nominator";
        }

    },

    watch: {
        dataStatus(status) {
            if (status === 'edit' || status === 'create') {
                this.getNomineeDirectorRequireData();
            }
        },

        isNomineeDirectorSidebarActive() {
            if ((this.dataStatus === 'edit' || this.dataStatus === 'create') && this.isNomineeDirectorSidebarActive === true)
            {
                this.getShareholders();
            }
        },

        dataAutoFillRequestedCorporateNominator()
        {
            if(this.dataAutoFillRequestedCorporateNominator)
            {
                this.nomineeDirectorLocal.nominatorType = { 
                    text: SHAREHOLDER.TYPE.CORPORATE,
                    value: SHAREHOLDER.TYPE.CORPORATE
                };
                
                this.nomineeDirectorLocal.uen = this.dataAutoFillRequestedCorporateNominator.uen;
                this.nomineeDirectorLocal.statue = this.dataAutoFillRequestedCorporateNominator?.corporate_shareholder?.statue;
                this.nomineeDirectorLocal.registrar= this.dataAutoFillRequestedCorporateNominator?.corporate_shareholder?.registrar;
                this.nomineeDirectorLocal.jurisdiction = this.dataAutoFillRequestedCorporateNominator?.corporate_shareholder?.jurisdiction;
                this.nomineeDirectorLocal.legalForm = this.dataAutoFillRequestedCorporateNominator?.corporate_shareholder?.legal_form ? {
                    value: this.dataAutoFillRequestedCorporateNominator.corporate_shareholder.legal_form,
                    text: this.dataAutoFillRequestedCorporateNominator.corporate_shareholder.legal_form
                }: null;
            }
        },
    },

    async created(){
        this.reInitializeNominators(this.nomineeDirectorLocal.nominatorType ? this.nomineeDirectorLocal.nominatorType.text : SHAREHOLDER.TYPE.INDIVIDUAL);
    },

    methods: {
        async getNomineeDirectorRequireData() {
            this.clearExistingNomineeData();

            // get corporate data
            const payload = await Nominator.getCorporateData();
            this.jurisdictions = payload.jurisdiction;
            this.statues = payload.statue;
            this.dataStatues = this.statues;
            this.legalForms = payload.legal_form.map(lf => ({
                text: lf,
                value: lf,
            }));
            this.registrars = payload.registrar;
            this.dataRegistrars = this.registrars;

            // get nominee director
            const nomineeDirectors = await Director.getOnlyDirectors();
            nomineeDirectors.forEach(nomineeDirector => {
                // check if user_id already exists in array
                this.nomineeDirectorOptions.push({
                    id: nomineeDirector.user_id,
                    name: nomineeDirector.user?.user_info?.legal_name ?? nomineeDirector.user.name,
                });
            });

            // get nominator type options
            const nominatorTypeOptionsData = await CompanyNomineeDirector.getNominatorTypeOptions();
            this.nominatorTypeOptions = nominatorTypeOptionsData.map(opt => {
                const text  = opt;
                const value  = opt;
                return { text, value };
            });
        },

        async getShareholders() {
            // get shareholders
            this.dataShareholders = [];
            const { shareholders } = await Shareholder.all();

            shareholders.forEach(nominator => {
                this.dataShareholders.push({
                    id: nominator.reference_id,
                    shareholderType: nominator.shareholder_type,
                    name: nominator.user?.user_info?.legal_name ?? nominator.user.name,
                    email: nominator.user.email,
                    alias: nominator.user?.individual_shareholder?.alias
                });
            });

            this.reInitializeNominators(this.dataNomineeDirector.nominatorType?.text);
        },

        clearExistingNomineeData() {
            this.statues = [];
            this.legalForms = [];
            this.registrars = [];
            this.dataStatues = [];
            this.jurisdictions = [];
            this.dataRegistrars = [];
            this.dataShareholders = [];
            this.nominatorTypeOptions = [];
            this.nomineeDirectorOptions = [];
        },

        // eslint-disable-next-line func-names
        onNominatorNameChange: debounce(function(nominator) {
            if (this.nomineeDirectorLocal.nominatorType.text === SHAREHOLDER.TYPE.INDIVIDUAL) {
                this.$refs.nominatorAlias.localValue = nominator.alias;
                this.nomineeDirectorLocal.alias = nominator.alias;
            }
            else this.$emit('request-autofill-corporate-nominator-data', nominator.value);
        }, 500),

        reInitializeNominators(type)
        { 
            this.nominators = this.dataShareholders.filter(shareholder => shareholder.shareholderType === type).map(shareholder => ({
                value: shareholder.id,
                text: shareholder.name,
                alias: shareholder.alias
            }));
        },

        async onNominatorTypeChange(payload)
        {
            this.nomineeDirectorLocal.alias = null;
            this.nomineeDirectorLocal.nominator = null;
            this.reInitializeNominators(payload.value);

            // clear and recheck validation if nominator type change
            this.clearForm();
        },

        onNominatorJurisdictionChange(payload)
        {
            this.statues = this.dataStatues.filter(statue => statue.jurisdiction_id === payload.id);
            this.registrars = this.dataRegistrars.filter(registrar => registrar.jurisdiction_id === payload.id);
            this.nomineeDirectorLocal.statue = '';
            this.nomineeDirectorLocal.registrar = '';
        },
    },

    setup(props, { emit }) {
        // eslint-disable-next-line no-shadow
        function useNomineeDirectorHandler(props, emit) {
            const nomineeDirectorLocal = ref(JSON.parse(JSON.stringify(props.dataNomineeDirector.value)));

            const resetNomineeDirectorLocal = () => {
                // eslint-disable-next-line no-use-before-define
                clearForm();
                nomineeDirectorLocal.value = JSON.parse(JSON.stringify(props.dataNomineeDirector.value));
            };

            watch(props.dataNomineeDirector, () => {
                resetNomineeDirectorLocal();
            });
           
            const onSubmit = () => {
                const nomineeDirectorData = JSON.parse(JSON.stringify(nomineeDirectorLocal));
                
                if (props.dataNomineeDirector.value.id) emit('update-nominee-director', nomineeDirectorData.value);
                else emit('add-nominee-director', nomineeDirectorData.value);
            };

            return {
                nomineeDirectorLocal, 

                // UI
                onSubmit,
                resetNomineeDirectorLocal,
            };
        };
         
        const {
            nomineeDirectorLocal,

            // UI
            onSubmit, 
            resetNomineeDirectorLocal
        } = useNomineeDirectorHandler(toRefs(props), emit);

        const { refFormObserver, getValidationState, resetForm, clearForm } = formValidation(nomineeDirectorLocal);

        function clearFormData() {
            resetNomineeDirectorLocal();
            clearForm();
        };

        return {
            // Add New
            onSubmit,
            nomineeDirectorLocal,

            // Form Validation
            resetForm,
            clearForm,
            refFormObserver,
            getValidationState, 
            
            // Methods
            clearFormData,
        };
    },
};
</script>

<style scope>
.nominee-director-create-or-edit .preloader {
    width: 15px;
    height: 15px;
}

.nominee-director-create-or-edit .preloader div:before {
    background: var(--primary-color);
}

.nominee-director-create-or-edit .action-button .preloader div:before {
    background: white;
}

.side-bar-header{
   background: #f6f6f6 !important;
 } 
 
/* select disabled color overwrite */
.vs--disabled .vs__dropdown-toggle {
    background: #efefef!important;
    cursor: context-menu!important;
}
.vs--disabled .vs__search {
    background: #efefef!important;
    cursor: context-menu!important;
}
.vs--disabled .vs__open-indicator {
    background: #efefef!important;
    cursor: context-menu!important;
}
.vs--disabled .vs__selected{
    cursor: context-menu!important;
}
</style>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
@import "@core/scss/vue/libs/vue-flatpicker.scss";
@import "@core/scss/vue/libs/quill.scss";
</style>